/**
 * Construit la vue "classement mi-temps" à partir de la liste complète enrichie.
 * On ne garde que les joueurs ayant joué la pause (halfTimeRank non nul), on re-mappe les
 * champs d'affichage sur le volet mi-temps, on trie par rang mi-temps, puis on fenêtre.
 */
export const buildHalfTimeView = (fullList = []) => {
    const ranked = fullList
        // Seuls les joueurs ayant RÉELLEMENT joué la pause (le moteur attribue un halfTimeRank
        // à tout le monde, même aux skippers à 0 pt → on filtre sur la présence d'un prono HT).
        .filter(r => r.betDisplayHalfTime != null)
        .sort((a, b) => (a.halfTimeRank ?? Number.MAX_SAFE_INTEGER) - (b.halfTimeRank ?? Number.MAX_SAFE_INTEGER))
        .map((r, i) => ({
            ...r,
            rank: i + 1, // re-numérotation propre parmi les seuls joueurs mi-temps
            points: r.halfTimePoints || 0,
            betDisplay: r.betDisplayHalfTime,
            quizBonus: r.halfTimeQuizBonus || 0, // bonus quiz figé à la pause (pour le ⚡)
        }));

    return getSmartView(ranked);
};

/**
 * Filtre la liste pour l'affichage "Smart View" (Top 3 + Autour de moi)
 */
export const getSmartView = (rankedList) => {
    const totalPlayers = rankedList.length;

    // Règle 0 : Si 10 joueurs ou moins, on affiche tout.
    if (totalPlayers <= 10) return rankedList;

    const myIndex = rankedList.findIndex(p => p.isMe);
    const indicesToShow = new Set();

    // 1. Toujours afficher le Top 3
    indicesToShow.add(0);
    indicesToShow.add(1);
    indicesToShow.add(2);

    // 2. Toujours afficher le Dernier
    indicesToShow.add(totalPlayers - 1);

    // 3. Logique des "Slots" restants
    if (myIndex < 8) {
        // CAS A : Je suis dans le top (ou proche), on affiche les 10 premiers
        // (Indices 0 à 9, car 0-2 sont déjà ajoutés, ça ne duplique pas le Set)
        for (let i = 0; i < 10; i++) {
            indicesToShow.add(i);
        }
    } else {
        // CAS B : Je suis loin (9ème ou plus)
        // On veut remplir jusqu'à avoir ~10 éléments au total.
        // On a déjà 4 éléments fixes (0,1,2,Last). Il en manque 6.
        // On essaie de centrer la vue sur moi : 4 avant, 1 après (moi inclus).

        let startWindow = myIndex - 4;
        let endWindow = myIndex + 1;

        // CORRECTION DE BORD (Safe) :
        // Si la fenêtre dépasse la fin (ex: je suis dernier), on décale le début vers le haut
        // pour garder une fenêtre de taille constante (6 items).
        // Max index possible avant le dernier = totalPlayers - 2
        if (endWindow >= totalPlayers - 1) {
            const overflow = endWindow - (totalPlayers - 2);
            startWindow -= overflow;
            endWindow = totalPlayers - 2; // On s'arrête juste avant le dernier (déjà ajouté)
        }

        // Sécurité : On ne remonte pas dans le Top 3
        if (startWindow < 3) startWindow = 3;

        for (let i = startWindow; i <= endWindow; i++) {
            indicesToShow.add(i);
        }
    }

    // 4. Construction et Tri
    const sortedIndices = Array.from(indicesToShow).sort((a, b) => a - b);
    const smartView = [];

    for (let i = 0; i < sortedIndices.length; i++) {
        const currentIndex = sortedIndices[i];
        const previousIndex = sortedIndices[i - 1];

        // LOGIQUE SÉPARATEUR
        if (i > 0 && currentIndex > previousIndex + 1) {
            smartView.push({id: `sep-${previousIndex}-${currentIndex}`, isSeparator: true});
        }

        smartView.push(rankedList[currentIndex]);
    }

    return smartView;
};